import scrollbarSize from 'dom-helpers/scrollbarSize'
import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { TableViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/TableViewColumnDefinition'
import React from 'react'
import { useLocalStore } from 'mobx-react-lite'
import { AutoSizer } from 'react-virtualized'
import { getRowWidth, getStickyColumnsStyle } from '@services/tableViewService'
import { SelectionCountRow } from './components/SelectionCountRow'
import { LetterJumpTo } from './components/LetterJumpTo'
import { TableHeader } from './components/TableHeader'
import { TableBody } from './components/TableBody'
import { SelectField } from '@components/UI-Components/SelectField'
import { OutlinedInput } from '@material-ui/core'
import { uniq } from 'lodash'
import { TableSearch } from './components/TableSearch'
import { StandardTableViewStore } from './stores'
import {
  SEARCHBARHEIGHT,
  SELECTIONCOUNTROWHEIGHT,
  STRIPCONTAINERHEIGHT
} from '../StandardTableView'
import { TableColumnFilter } from './components/TableColumnFilter'

interface IStandardTableViewProps {
  data: Array<any>
  fieldNameToJump?: string
  headerHeight: number
  rowHeight: number
  colDef: Array<TableViewColumnDefinition>
  useIsScrolling?: boolean
  overScanCount?: number
  hideHeader?: boolean
  showSearch?: boolean
  searchBarHeight?: number
  hideLetterNavigation?: boolean
  expandableSectionHeight?: number
  expandedRowKey?: string
  expandedRowComponent?: any
  tableCellFontSize?: any
  showBodyRowBorder?: boolean
  allowMultiSelect?: boolean
  maxSelectionLimit?: number
  setSelectedData?: any
  canSelectAll?: boolean
  showCountDetails?: boolean
  selectedRows?: any
  disabledRows?: any[]
  hideHeaderTopBorder?: boolean
  setFilterData?: any
  showCountDetailOnly?: boolean
  allowDisabledRowsSelection?: boolean
  headerData?: any
  selectedHeaderId?: any
  onHeaderDataSelect?: any
  isNewLayout?: boolean
  showAllFilter?: boolean
}

export const TableViewStoreContext = React.createContext<StandardTableViewStore | null>(
  null
)

export const StandardTableView: React.FunctionComponent<IStandardTableViewProps> = ({
  data,
  headerHeight,
  rowHeight,
  fieldNameToJump,
  colDef,
  useIsScrolling,
  overScanCount,
  hideHeader,
  showSearch,
  hideLetterNavigation,
  expandableSectionHeight,
  expandedRowKey,
  expandedRowComponent,
  tableCellFontSize,
  showBodyRowBorder,
  allowMultiSelect,
  maxSelectionLimit,
  setSelectedData,
  canSelectAll,
  showCountDetails = true,
  selectedRows,
  disabledRows,
  hideHeaderTopBorder,
  setFilterData,
  showCountDetailOnly,
  allowDisabledRowsSelection = true,
  headerData,
  selectedHeaderId,
  onHeaderDataSelect,
  showAllFilter
}) => {
  const store = useLocalStore(() => new StandardTableViewStore())

  React.useEffect(
    () => store.setAllowDisabledRowsSelection(allowDisabledRowsSelection),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowDisabledRowsSelection]
  )

  const filteredLetters = React.useMemo(() => {
    return uniq(data?.map(x => store.getInitialsAlphabet(x?.name)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const NewCenterLayout = ({
    widthWithoutScroll,
    width,
    tableRowWidth,
    colWiseStickyStyle
  }) => {
    return (
      <div>
        {!hideHeader || allowMultiSelect ? (
          <div style={{ marginTop: 12 }}>
            {!hideHeader || allowMultiSelect ? (
              <TableHeader
                visibleHeight={headerHeight}
                visibleWidth={widthWithoutScroll}
                rowHeight={headerHeight}
                rowWidth={tableRowWidth}
                stickyCols={colWiseStickyStyle}
                colDef={colDef}
                allowMultiSelect={allowMultiSelect}
                hideHeader={hideHeader}
                maxSelectionLimit={maxSelectionLimit}
                canSelectAll={canSelectAll}
                hideHeaderTopBorder={true}
                showAllFilter={showAllFilter}
                allRowsDisabled={disabledRows?.length === data?.length}
              />
            ) : null}
          </div>
        ) : null}
        <div style={{ marginTop: 12 }}></div>
        {showSearch && <TableSearch width={width - 10} height={SEARCHBARHEIGHT} />}
      </div>
    )
  }

  return (
    <TableViewStoreContext.Provider value={store}>
      <AutoSizer>
        {({ height, width }) => {
          if (width <= 0 || height <= 0) return <></>
          const widthWithoutScroll = width - scrollbarSize()
          const tableBodyVisibleHeight =
            height -
            (allowMultiSelect
              ? hideLetterNavigation
                ? SELECTIONCOUNTROWHEIGHT
                : SELECTIONCOUNTROWHEIGHT - 10
              : 0) -
            (hideLetterNavigation ? 0 : STRIPCONTAINERHEIGHT) -
            (hideHeader && !canSelectAll ? 0 : headerHeight) -
            (showSearch ? SEARCHBARHEIGHT : 0)
          const tableRowWidth = getRowWidth(
            colDef as any[],
            null as any,
            allowMultiSelect as boolean
          )
          const colWiseStickyStyle = getStickyColumnsStyle(
            colDef as any[],
            allowMultiSelect as boolean
          )
          return data && data.length ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                {headerData && headerData.length ? (
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <SelectField
                      customWidth={200}
                      isUpperCase={true}
                      input={
                        <OutlinedInput
                          notched={false}
                          labelWidth={200}
                          id='filterItems'
                        />
                      }
                      id={'filterItems'}
                      value={selectedHeaderId}
                      options={headerData}
                      onChange={event => {
                        store?.setHeaderSelection(true)
                        onHeaderDataSelect(event)
                      }}
                    />
                    <div style={{ marginLeft: '24px' }}></div>
                  </div>
                ) : null}

                {allowMultiSelect && showCountDetails ? (
                  <SelectionCountRow
                    showCountDetailOnly={showCountDetailOnly}
                    setFilterData={setFilterData}
                    maxSelectionLimit={maxSelectionLimit as number}
                    height={
                      hideLetterNavigation
                        ? SELECTIONCOUNTROWHEIGHT
                        : SELECTIONCOUNTROWHEIGHT - 10
                    }
                    width={widthWithoutScroll}
                  />
                ) : null}
              </div>
              <NewCenterLayout
                widthWithoutScroll={widthWithoutScroll}
                width={width}
                tableRowWidth={tableRowWidth}
                colWiseStickyStyle={colWiseStickyStyle}
              />
              {data.length ? (
                <div style={{ flexDirection: 'row', display: 'inline-flex' }}>
                  <TableColumnFilter />
                  <TableBody
                    overScanCount={overScanCount}
                    data={data}
                    useIsScrolling={false}
                    colDef={colDef}
                    visibleHeight={tableBodyVisibleHeight}
                    visibleWidth={width - 20}
                    rowWidth={tableRowWidth}
                    rowHeight={rowHeight}
                    expandableSectionHeight={expandableSectionHeight}
                    expandedRowKey={expandedRowKey}
                    expandedRowComponent={expandedRowComponent}
                    stickyCols={colWiseStickyStyle}
                    fieldNameToJump={fieldNameToJump}
                    showBodyRowBorder={showBodyRowBorder}
                    tableCellFontSize={tableCellFontSize}
                    allowMultiSelect={allowMultiSelect}
                    maxSelectionLimit={maxSelectionLimit}
                    setSelectedData={setSelectedData}
                    selectedData={selectedRows}
                    disabledRowData={disabledRows}
                  />

                  {!hideLetterNavigation && fieldNameToJump && data.length && (
                    <LetterJumpTo
                      data={filteredLetters}
                      stripWidth={widthWithoutScroll}
                    />
                  )}
                </div>
              ) : (
                <NoRecordFound />
              )}
            </>
          ) : (
            <NoRecordFound />
          )
        }}
      </AutoSizer>
    </TableViewStoreContext.Provider>
  )
}
